<template>
  <div>
    <section class="bridge-card">
      <header class="bridge-header">
        <div>
          <h2 class="bridge-title">Products</h2>
          <p class="bridge-description">
            Mirrors Stripe Products. Changes are written to Stripe, then re-mirrored to Postgres automatically.
          </p>
        </div>
        <div class="bridge-toolbar">
          <aha-status-badge v-if="mode" :look="isLive ? 'alert' : 'quiet'" :aria-label="modeLabel + ' mode'">{{ modeLabel }} mode</aha-status-badge>
          <label class="bridge-switch">
            <aha-switch
              size="small"
              aria-label="Show archived"
              :checked.prop="includeArchived"
              :disabled.prop="isLoading || !!busyKey"
              @change="toggleArchived"
            ></aha-switch>
            Show archived
          </label>
          <aha-button size="sm" :disabled="isLoading || !!busyKey" :loading="isLoading" @click="loadCatalog">Reload</aha-button>
        </div>
      </header>

      <aha-alert v-if="notice" type="warning" closable class="bridge-block" @close="notice = ''">{{ notice }}</aha-alert>
      <p v-if="isLoading && !loaded" class="bridge-muted" role="status">Loading the catalogue from Stripe</p>
      <aha-alert v-else-if="loadError" type="error" class="bridge-block">
        {{ loadError }}
        <aha-button slot="action" size="sm" @click="loadCatalog">Try again</aha-button>
      </aha-alert>
      <template v-else-if="loaded">
        <h3 class="bridge-subtitle">All products ({{ products.length }})</h3>
        <p v-if="!products.length" class="bridge-muted">Stripe returned no products.</p>
        <div v-else class="bridge-table-wrap">
          <table class="bridge-table">
            <thead>
              <tr><th>Product</th><th>Metadata</th><th>Prices</th><th>Actions</th></tr>
            </thead>
            <tbody>
              <tr v-for="product in products" :key="product.id">
                <td>
                  <span class="bridge-cell-strong">{{ product.name }}</span>
                  <aha-status-badge v-if="!product.active" size="sm" look="quiet">Archived</aha-status-badge>
                  <span class="bridge-cell-sub"><code>{{ product.id }}</code></span>
                  <span class="bridge-cell-sub">{{ attachedFeatureIds(product.id).length }} features</span>
                </td>
                <td>
                  <ul v-if="metadataRows(product).length" class="bridge-kv">
                    <li v-for="row in metadataRows(product)" :key="row.key"><strong>{{ row.key }}</strong>: {{ row.value }}</li>
                  </ul>
                  <span v-else class="bridge-muted">None</span>
                  <aha-button size="xs" variant="tertiary" :disabled="!!busyKey" @click="openMetadata(product)">Edit metadata</aha-button>
                </td>
                <td>
                  <ul v-if="activePrices(product).length" class="bridge-kv">
                    <li v-for="price in activePrices(product)" :key="price.id">
                      {{ formatPrice(price) }}
                      <span v-if="price.id === product.defaultPriceId" class="bridge-muted">(default)</span>
                      <span class="bridge-cell-sub"><code>{{ price.id }}</code><template v-if="price.lookupKey"> · <code>{{ price.lookupKey }}</code></template></span>
                    </li>
                  </ul>
                  <span v-else class="bridge-muted">No active prices</span>
                </td>
                <td>
                  <div class="bridge-row-actions">
                    <aha-button size="sm" variant="secondary" :disabled="!!busyKey" @click="openFeatures(product)">Edit features</aha-button>
                    <aha-button
                      size="sm"
                      variant="secondary"
                      :disabled="!!busyKey || !activePrices(product).length"
                      @click="openPrice(product)"
                    >Change price</aha-button>
                    <aha-button
                      v-if="product.active"
                      size="sm"
                      variant="danger"
                      :disabled="!!busyKey"
                      @click="openArchive(product, true)"
                    >Archive</aha-button>
                    <aha-button v-else size="sm" variant="secondary" :disabled="!!busyKey" @click="openArchive(product, false)">Unarchive</aha-button>
                    <aha-button v-if="product.deletable" size="sm" variant="danger" :disabled="!!busyKey" @click="openDelete(product)">Delete</aha-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </section>

    <stripe-bridge-dialog :open="dialog === 'features'" :title="target ? 'Features on ' + target.name : 'Features'" :busy="!!busyKey" @close="closeDialog">
      <stripe-bridge-link-editor
        v-if="target"
        heading="Features on this product"
        empty-text="This product has no features."
        pick-label="Attach features"
        picker-label="Features"
        placeholder="Search by feature name or lookup key"
        :options="featureOptions"
        :attached-ids="attachedFeatureIds(target.id)"
        :busy="busyKey === 'link'"
        :errors="linkErrors"
        :message="linkMessage"
        @attach="attachFeatures"
        @detach="detachFeature"
      ></stripe-bridge-link-editor>
      <template slot="footer">
        <aha-button variant="secondary" :disabled="!!busyKey" @click="closeDialog">Done</aha-button>
      </template>
    </stripe-bridge-dialog>

    <stripe-bridge-confirm
      :open="dialog === 'price'"
      title="Change price"
      ok-label="Create new price"
      :busy="busyKey === 'price'"
      :error="dialogError"
      :require-text="isLive && target ? target.name : ''"
      :disabled="!canSubmitPrice"
      @cancel="closeDialog"
      @confirm="runPrice"
    >
      <template v-if="target && currentPrice">
        <p>
          Change the price of <strong>{{ target.name }}</strong> in Stripe
          <aha-status-badge size="sm" :look="isLive ? 'alert' : 'quiet'">{{ modeLabel }}</aha-status-badge>.
        </p>
        <div v-if="activePrices(target).length > 1" class="bridge-field">
          <span class="bridge-label">Price to change</span>
          <aha-select
            aria-label="Price to change"
            :options="priceSelectOptions"
            :value="priceId"
            :disabled.prop="!!busyKey"
            @change="priceId = detailValue($event, priceId)"
          ></aha-select>
        </div>
        <p class="bridge-muted">Current price: {{ formatPrice(currentPrice) }}</p>
        <div class="bridge-field">
          <span class="bridge-label">New amount ({{ currentPrice.currency.toUpperCase() }})</span>
          <aha-input
            aria-label="New amount"
            inputmode="decimal"
            placeholder="For example, 9.99"
            :value="newAmount"
            :status="newAmount && newAmountMinor === null ? 'error' : 'default'"
            :error-message="newAmount && newAmountMinor === null ? amountError : ''"
            :disabled.prop="!!busyKey"
            @input="newAmount = detailValue($event, newAmount)"
          ></aha-input>
          <span v-if="newAmountMinor !== null" class="bridge-muted">Sent to Stripe as {{ newAmountMinor }} (minor units).</span>
        </div>
        <div v-for="entry in currencyEntries" :key="entry.currency" class="bridge-field">
          <span class="bridge-label">New amount ({{ entry.currency.toUpperCase() }})</span>
          <aha-input
            v-if="entry.minor !== null"
            :aria-label="'New amount in ' + entry.currency.toUpperCase()"
            inputmode="decimal"
            :value="currencyAmounts[entry.currency]"
            :status="currencyMinor(entry.currency) === null ? 'error' : 'default'"
            :error-message="currencyMinor(entry.currency) === null ? amountError : ''"
            :disabled.prop="!!busyKey"
            @input="$set(currencyAmounts, entry.currency, detailValue($event, currencyAmounts[entry.currency]))"
          ></aha-input>
          <span v-else class="bridge-muted">Not a flat amount</span>
        </div>
        <p v-if="currencyEntries.length" class="bridge-muted">Other currencies keep their amounts unless you change them.</p>
        <aha-alert type="info" size="small">
          Stripe prices cannot be edited. This creates a new price with the new amount, moves the lookup key
          <template v-if="currentPrice.lookupKey">(<code>{{ currentPrice.lookupKey }}</code>)</template>
          to it and archives the old price. Current subscribers keep the old price.
        </aha-alert>
      </template>
    </stripe-bridge-confirm>

    <stripe-bridge-confirm
      :open="dialog === 'archive'"
      :title="archiveIntent ? 'Archive this product?' : 'Unarchive this product?'"
      :ok-label="archiveIntent ? 'Archive product' : 'Unarchive product'"
      :danger="archiveIntent"
      :busy="busyKey === 'archive'"
      :error="dialogError"
      :require-text="isLive && archiveIntent && target ? target.name : ''"
      @cancel="closeDialog"
      @confirm="runArchive"
    >
      <template v-if="target">
        <p>
          {{ archiveIntent ? 'Archive' : 'Unarchive' }} <strong>{{ target.name }}</strong> in Stripe
          <aha-status-badge size="sm" :look="isLive ? 'alert' : 'quiet'">{{ modeLabel }}</aha-status-badge>?
        </p>
        <aha-alert v-if="archiveIntent" type="warning" size="small">
          Existing subscriptions keep running, but payment links for this product stop working. Products that have prices can only be archived.
        </aha-alert>
        <p v-else class="bridge-muted">The product becomes available for new payment links and subscriptions again.</p>
      </template>
    </stripe-bridge-confirm>

    <stripe-bridge-metadata-editor
      :open="dialog === 'metadata'"
      :item="target"
      empty-text="This product has no metadata."
      :mode="mode"
      :busy="busyKey === 'metadata'"
      :error="dialogError"
      @cancel="closeDialog"
      @save="runMetadata"
    ></stripe-bridge-metadata-editor>

    <stripe-bridge-confirm
      :open="dialog === 'delete'"
      title="Delete this product?"
      ok-label="Delete product"
      danger
      :busy="busyKey === 'delete'"
      :error="dialogError"
      :require-text="isLive && target ? target.name : ''"
      @cancel="closeDialog"
      @confirm="runDelete"
    >
      <template v-if="target">
        <p>
          Delete <strong>{{ target.name }}</strong> from Stripe
          <aha-status-badge size="sm" :look="isLive ? 'alert' : 'quiet'">{{ modeLabel }}</aha-status-badge>?
        </p>
        <aha-alert type="warning" size="small">This permanently deletes {{ target.name }} from Stripe. This cannot be undone.</aha-alert>
      </template>
    </stripe-bridge-confirm>
  </div>
</template>

<script>
import Config from '@/config'
import utils from '@/utils'
import catalogMixin, { errorMessage, formatPrice, toMajorUnits, toMinorUnits } from './catalogMixin'
import nativeAhaElements from './nativeAhaElements'
import StripeBridgeConfirm from './StripeBridgeConfirm'
import StripeBridgeDialog from './StripeBridgeDialog'
import StripeBridgeLinkEditor from './StripeBridgeLinkEditor'
import StripeBridgeMetadataEditor from './StripeBridgeMetadataEditor'

export default {
  name: 'StripeBridgeProducts',
  components: { ...nativeAhaElements, StripeBridgeConfirm, StripeBridgeDialog, StripeBridgeLinkEditor, StripeBridgeMetadataEditor },
  mixins: [catalogMixin],
  data() {
    return {
      dialog: '',
      dialogError: '',
      target: null,
      linkErrors: {},
      linkMessage: null,
      priceId: '',
      newAmount: '',
      currencyAmounts: {},
      amountError: 'Enter an amount of 0 or more',
      archiveIntent: true,
    }
  },
  computed: {
    featureOptions() {
      return this.features.map(f => ({ id: f.id, label: f.name, sub: f.lookupKey, active: f.active }))
    },
    currentPrice() {
      return this.target ? this.activePrices(this.target).find(p => p.id === this.priceId) || null : null
    },
    newAmountMinor() {
      return this.currentPrice ? toMinorUnits(this.newAmount, this.currentPrice.currency) : null
    },
    currencyEntries() {
      return this.currentPrice
        ? Object.entries(this.currentPrice.currencyOptions || {}).map(([currency, minor]) => ({ currency, minor: minor == null ? null : minor }))
        : []
    },
    changedCurrencyOptions() {
      const changed = {}
      this.currencyEntries.forEach(({ currency, minor }) => {
        const next = this.currencyMinor(currency)
        if (minor !== null && next !== null && next !== minor) changed[currency] = next
      })
      return changed
    },
    canSubmitPrice() {
      if (this.newAmountMinor === null) return false
      if (this.currencyEntries.some(({ currency, minor }) => minor !== null && this.currencyMinor(currency) === null)) return false
      return this.newAmountMinor !== this.currentPrice.unitAmount || Object.keys(this.changedCurrencyOptions).length > 0
    },
    priceSelectOptions() {
      return JSON.stringify(this.activePrices(this.target).map(p => ({ value: p.id, label: `${formatPrice(p)} (${p.id})` })))
    },
  },
  watch: {
    priceId() {
      this.resetAmounts()
    },
  },
  methods: {
    formatPrice,
    currencyMinor(currency) {
      return toMinorUnits(this.currencyAmounts[currency], currency)
    },
    resetAmounts() {
      const price = this.currentPrice
      this.newAmount = price && price.unitAmount != null ? toMajorUnits(price.unitAmount, price.currency) : ''
      this.currencyAmounts = Object.fromEntries(
        this.currencyEntries.filter(({ minor }) => minor !== null).map(({ currency, minor }) => [currency, toMajorUnits(minor, currency)])
      )
    },
    metadataRows(product) {
      return Object.entries(product.metadata || {}).map(([key, value]) => ({ key, value }))
    },
    activePrices(product) {
      return ((product && product.prices) || []).filter(p => p.active !== false)
    },
    closeDialog() {
      if (this.busyKey) return
      this.dialog = ''
      this.dialogError = ''
    },
    openFeatures(product) {
      this.target = product
      this.linkErrors = {}
      this.linkMessage = null
      this.dialog = 'features'
    },
    async attachFeatures(featureIds) {
      if (this.busyKey || !featureIds.length) return
      this.busyKey = 'link'
      this.linkErrors = {}
      this.linkMessage = null
      const done = []
      const unmirrored = []
      const errors = {}
      for (const featureId of featureIds) {
        try {
          const body = await this.attachPair(this.target.id, featureId)
          const name = this.featureById[featureId].name
          done.push(name)
          if (body && body.mirrored === false) unmirrored.push(name)
        } catch (error) {
          errors[featureId] = errorMessage(error, 'Could not attach. Please try again.')
        }
      }
      this.linkErrors = errors
      this.busyKey = ''
      const failed = Object.keys(errors).length
      if (done.length) this.linkMessage = { type: failed ? 'warning' : 'success', text: `Attached ${done.join(', ')} to ${this.target.name}.${failed ? ` ${failed} failed.` : ''}` }
      else if (failed) this.linkMessage = { type: 'error', text: 'Nothing was attached. See the errors in the list.' }
      if (unmirrored.length) this.notice = this.unmirroredNotice(unmirrored)
    },
    async detachFeature(featureId) {
      if (this.busyKey) return
      this.busyKey = 'link'
      this.linkMessage = null
      try {
        const body = await this.detachPair(this.target.id, featureId)
        if (body && body.mirrored === false) this.notice = this.unmirroredNotice([this.featureById[featureId].name])
        else utils.showToastSuccess('Detached. Mirror updated.')
      } catch (error) {
        this.linkMessage = { type: 'error', text: errorMessage(error, 'Could not detach. Please try again.') }
      } finally {
        this.busyKey = ''
      }
    },
    openPrice(product) {
      this.target = product
      const prices = this.activePrices(product)
      const preferred = prices.find(p => p.id === product.defaultPriceId) || prices[0]
      this.priceId = preferred ? preferred.id : ''
      this.resetAmounts()
      this.dialogError = ''
      this.dialog = 'price'
    },
    async runPrice() {
      if (this.busyKey || this.newAmountMinor === null) return
      this.busyKey = 'price'
      this.dialogError = ''
      const product = this.target
      try {
        const url = `${Config.api.stripeMirrorProducts}/${encodeURIComponent(product.id)}/prices/${encodeURIComponent(this.priceId)}/replace`
        const res = await this.$http.post(url, {
          unitAmount: this.newAmountMinor,
          ...(Object.keys(this.changedCurrencyOptions).length ? { currencyOptions: this.changedCurrencyOptions } : {}),
        })
        this.busyKey = ''
        this.dialog = ''
        if (res.body && res.body.mirrored === false) this.notice = this.unmirroredNotice([`${product.name} price`])
        else utils.showToastSuccess(`${product.name} has a new price. The old one is archived.`)
        await this.loadCatalog()
      } catch (error) {
        this.dialogError = errorMessage(error, 'Could not change the price. Please try again.')
      } finally {
        this.busyKey = ''
      }
    },
    openMetadata(product) {
      this.target = product
      this.dialogError = ''
      this.dialog = 'metadata'
    },
    async runMetadata(metadata) {
      if (this.busyKey) return
      this.busyKey = 'metadata'
      this.dialogError = ''
      const product = this.target
      try {
        const res = await this.$http.patch(`${Config.api.stripeMirrorProducts}/${encodeURIComponent(product.id)}/metadata`, { metadata })
        this.busyKey = ''
        this.dialog = ''
        if (res.body && res.body.product) this.products = this.products.map(p => (p.id === product.id ? { ...p, metadata: res.body.product.metadata || {} } : p))
        if (res.body && res.body.mirrored === false) this.notice = this.unmirroredNotice([`${product.name} metadata`])
        else utils.showToastSuccess(`${product.name} metadata saved.`)
      } catch (error) {
        this.dialogError = errorMessage(error, 'Could not save the metadata. Please try again.')
      } finally {
        this.busyKey = ''
      }
    },
    openDelete(product) {
      this.target = product
      this.dialogError = ''
      this.dialog = 'delete'
    },
    async runDelete() {
      if (this.busyKey) return
      this.busyKey = 'delete'
      this.dialogError = ''
      const product = this.target
      try {
        const res = await this.$http.delete(`${Config.api.stripeMirrorProducts}/${encodeURIComponent(product.id)}`)
        this.busyKey = ''
        this.dialog = ''
        if (res.body && res.body.mirrored === false) this.notice = this.unmirroredNotice([product.name], 'deleted')
        else utils.showToastSuccess(`${product.name} deleted from Stripe.`)
        await this.loadCatalog()
      } catch (error) {
        this.dialogError = errorMessage(error, 'Could not delete the product. Please try again.')
      } finally {
        this.busyKey = ''
      }
    },
    openArchive(product, archive) {
      this.target = product
      this.archiveIntent = archive
      this.dialogError = ''
      this.dialog = 'archive'
    },
    async runArchive() {
      if (this.busyKey) return
      this.busyKey = 'archive'
      this.dialogError = ''
      const product = this.target
      const action = this.archiveIntent ? 'archive' : 'unarchive'
      try {
        const res = await this.$http.post(`${Config.api.stripeMirrorProducts}/${encodeURIComponent(product.id)}/${action}`, {})
        this.busyKey = ''
        this.dialog = ''
        if (res.body && res.body.mirrored === false) this.notice = this.unmirroredNotice([product.name])
        else utils.showToastSuccess(`${product.name} ${this.archiveIntent ? 'archived' : 'unarchived'} in Stripe.`)
        await this.loadCatalog()
      } catch (error) {
        this.dialogError = errorMessage(error, `Could not ${action} the product. Please try again.`)
      } finally {
        this.busyKey = ''
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './bridgeStyles.scss';
</style>
